import { useLayoutEffect } from 'react';
import { useReducedMotion } from './useMotion';

const clamp = value => Math.min(1, Math.max(0, value));
const ease = value => value * value * (3 - 2 * value);

// Measure stable wrappers; reading statements follow scroll position while
// ordinary headings and team copy retain their completed reveals.
export default function useScrollScene(rootRef, brandSpaceRef, scienceRef, visualizationRef) {
  const reducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const root = rootRef.current;
    const brandSpace = brandSpaceRef.current;
    const science = scienceRef.current;
    if (!root || !brandSpace || !science) return;
    const scienceLogoSpace = science.querySelector('.science__logo-space');
    const lines = [...root.querySelectorAll('[data-reveal]')];
    const readingLines = [...root.querySelectorAll('[data-reading-line]')];
    const popItems = [...root.querySelectorAll('[data-pop]')];
    const dismissItems = [...root.querySelectorAll('[data-dismiss]')];
    const pending = new Set(lines.filter(line => !line.classList.contains('is-revealed')));
    let frame = null;
    let disposed = false;
    let needsMeasure = true;
    let brandTop = 0;
    let brandWidth = 1;
    let dockedSize = 112;
    let markBottom = .756;
    let topInset = 0;
    let shrinkLead = 48;
    let shrinkDistance = 160;
    let dismissDistance = 32;
    let readingScaleEnd = 1.18;
    let scienceDockedWidth = 180;
    let scienceDockGap = 12;
    let scienceShrinkDistance = 180;

    const measure = () => {
      const bounds = brandSpace.getBoundingClientRect();
      // A first layout or a temporarily hidden page can report an empty box.
      // Keep the CSS fallback/last good position until the placeholder is real.
      if (!Number.isFinite(bounds.top) || !Number.isFinite(bounds.width)
        || !Number.isFinite(bounds.height) || bounds.width <= 0 || bounds.height <= 0) return false;
      const styles = getComputedStyle(root);
      brandTop = bounds.top + window.scrollY;
      brandWidth = bounds.width;
      dockedSize = parseFloat(styles.getPropertyValue('--brand-docked-size')) || 112;
      markBottom = parseFloat(styles.getPropertyValue('--brand-mark-bottom')) || .756;
      topInset = parseFloat(styles.getPropertyValue('--brand-top-inset')) || 0;
      shrinkLead = parseFloat(styles.getPropertyValue('--brand-shrink-lead')) || 48;
      shrinkDistance = parseFloat(styles.getPropertyValue('--brand-shrink-distance')) || 160;
      dismissDistance = parseFloat(styles.getPropertyValue('--dismiss-distance')) || 32;
      readingScaleEnd = parseFloat(styles.getPropertyValue('--reading-scale-end')) || 1.18;
      scienceDockedWidth = parseFloat(styles.getPropertyValue('--science-docked-width')) || 180;
      scienceDockGap = parseFloat(styles.getPropertyValue('--science-dock-gap')) || 12;
      scienceShrinkDistance = parseFloat(styles.getPropertyValue('--science-shrink-distance')) || 180;
      return true;
    };

    const update = () => {
      frame = null;
      if (disposed) return;
      const viewport = window.innerHeight;
      const scroll = window.scrollY;
      if (!Number.isFinite(viewport) || viewport <= 0 || !Number.isFinite(scroll)) return;
      if (needsMeasure) {
        if (!measure()) return;
        needsMeasure = false;
      }
      const travel = Math.max(1, brandTop - topInset);
      const progress = ease(clamp((scroll - Math.max(0, travel - shrinkLead)) / shrinkDistance));
      const scale = reducedMotion
        ? (scroll >= travel ? dockedSize / brandWidth : 1)
        : 1 - progress * (1 - dockedSize / brandWidth);
      const brandBottom = topInset + dockedSize * markBottom;
      const scienceLogoBounds = scienceLogoSpace?.getBoundingClientRect();
      let scienceLogoFrame = null;
      let contentTop = brandBottom + 4;
      if (scienceLogoBounds && Number.isFinite(scienceLogoBounds.top)
        && Number.isFinite(scienceLogoBounds.width) && scienceLogoBounds.width > 0
        && Number.isFinite(scienceLogoBounds.height) && scienceLogoBounds.height > 0) {
        const dockTop = brandBottom + scienceDockGap;
        const dockWidth = Math.min(scienceDockedWidth, scienceLogoBounds.width);
        const dockProgress = reducedMotion
          ? (scienceLogoBounds.top <= dockTop ? 1 : 0)
          : ease(clamp((dockTop + scienceShrinkDistance - scienceLogoBounds.top) / scienceShrinkDistance));
        scienceLogoFrame = {
          fixed: dockProgress > 0,
          width: scienceLogoBounds.width,
          y: Math.max(dockTop, scienceLogoBounds.top),
          scale: 1 - dockProgress * (1 - dockWidth / scienceLogoBounds.width),
        };
        contentTop += dockProgress * (scienceDockGap + dockWidth * scienceLogoBounds.height / scienceLogoBounds.width);
      }
      const dismissalOpacity = top => ease(clamp((top - contentTop) / dismissDistance));

      // Read before writing styles. The reveal trigger is independent of the
      // animated child, so its small upward movement cannot retrigger itself.
      const entering = [...pending].filter(line => reducedMotion || line.getBoundingClientRect().top <= viewport * .82);
      const readingFrames = readingLines.map(line => {
        const bounds = line.getBoundingClientRect();
        const center = (bounds.top + bounds.height / 2) / viewport;
        // Enter near the bottom with breathing room; finish fading in before
        // the entrance scale settles at the screen center.
        const entryStart = .875;
        const entry = ease(clamp((entryStart - center) / .15));
        const motion = ease(clamp((entryStart - center) / (entryStart - .5)));
        const textHeight = line.firstElementChild?.offsetHeight || 0;
        // Grow once the phrase passes center, then fade above the reading area.
        // Use the largest text height to finish before it reaches the logo.
        const exitEnd = Math.max(.18, (contentTop + textHeight * readingScaleEnd / 2) / viewport);
        const departure = ease(clamp((.5 - center) / Math.max(.01, .5 - exitEnd)));
        const exitOpacity = 1 - ease(clamp((.45 - center) / Math.max(.01, .45 - exitEnd)));
        const textScale = 1 + departure * (readingScaleEnd - 1);
        const textTop = bounds.top + (bounds.height - textHeight * textScale) / 2;
        return {
          line,
          phase: 1 - center,
          opacity: reducedMotion ? 1 : Math.min(entry, exitOpacity, dismissalOpacity(textTop)),
          offset: reducedMotion ? 0 : 1 - motion,
          departure: reducedMotion ? 0 : departure,
        };
      });
      let previousTop = null;
      let column = 0;
      const popFrames = popItems.map(item => {
        const bounds = item.getBoundingClientRect();
        column = previousTop === null || Math.abs(bounds.top - previousTop) > 2 ? 0 : column + 1;
        previousTop = bounds.top;
        const center = (bounds.top + bounds.height / 2) / viewport;
        const start = .90 - column * .08;
        const opacity = ease(clamp((start - center) / .10));
        const motion = ease(clamp((start - center) / .14));
        return { item, opacity: reducedMotion ? 1 : opacity, offset: reducedMotion ? 0 : 1 - motion };
      });
      const dismissFrames = dismissItems.map(item => ({
        item,
        opacity: dismissalOpacity(item.getBoundingClientRect().top),
      }));
      const bounds = science.getBoundingClientRect();
      const fadeIn = ease(clamp((viewport - bounds.top) / (viewport * .7)));
      const fadeOut = ease(clamp(bounds.bottom / (viewport * .7)));

      root.style.setProperty('--brand-y', `${Math.max(topInset, brandTop - scroll)}px`);
      root.style.setProperty('--brand-scale', String(scale));
      if (scienceLogoFrame) {
        root.style.setProperty('--science-logo-width', `${scienceLogoFrame.width}px`);
        root.style.setProperty('--science-logo-y', `${scienceLogoFrame.y}px`);
        root.style.setProperty('--science-logo-scale', String(scienceLogoFrame.scale));
      }
      root.classList.toggle('science-logo-fixed', Boolean(scienceLogoFrame?.fixed));
      root.style.setProperty('--film-opacity', String(Math.min(fadeIn, fadeOut)));
      if (visualizationRef) {
        visualizationRef.current = {
          phases: Object.fromEntries(readingFrames
            .filter(({ line }) => line.dataset.visualScene)
            .map(({ line, phase }) => [line.dataset.visualScene, phase])),
          filmOpacity: Math.min(fadeIn, fadeOut),
        };
      }
      readingFrames.forEach(({ line, opacity, offset, departure }) => {
        line.style.setProperty('--reading-opacity', String(opacity));
        line.style.setProperty('--reading-entry', String(offset));
        line.style.setProperty('--reading-exit', String(departure));
      });
      popFrames.forEach(({ item, opacity, offset }) => {
        item.style.setProperty('--pop-opacity', String(opacity));
        item.style.setProperty('--pop-entry', String(offset));
      });
      dismissFrames.forEach(({ item, opacity }) => {
        item.style.setProperty('--dismiss-opacity', String(opacity));
      });
      entering.forEach(line => {
        line.classList.add('is-revealed');
        pending.delete(line);
      });
      root.classList.add('motion-ready');
    };

    const schedule = () => {
      if (!disposed && frame === null) frame = window.requestAnimationFrame(update);
    };
    const refresh = () => { needsMeasure = true; schedule(); };
    const fonts = document.fonts;
    const viewport = window.visualViewport;
    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(refresh) : null;
    if (resizeObserver) {
      new Set([root, brandSpace, brandSpace.closest('.hero'), science, scienceLogoSpace].filter(Boolean))
        .forEach(element => resizeObserver.observe(element));
    }
    // Place the logo before paint, then recheck once after the initial layout.
    update();
    refresh();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', refresh);
    window.addEventListener('load', refresh);
    window.addEventListener('pageshow', refresh);
    root.addEventListener('load', refresh, true);
    if (viewport) viewport.addEventListener('resize', refresh);
    if (fonts) {
      fonts.addEventListener?.('loadingdone', refresh);
      fonts.ready?.then(refresh, refresh);
    }
    return () => {
      disposed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('load', refresh);
      window.removeEventListener('pageshow', refresh);
      root.removeEventListener('load', refresh, true);
      if (viewport) viewport.removeEventListener('resize', refresh);
      if (fonts) fonts.removeEventListener?.('loadingdone', refresh);
    };
  }, [rootRef, brandSpaceRef, scienceRef, visualizationRef, reducedMotion]);
}
